// Contestações das avaliações da IA (Memor.IA) — visão do adm/gestor.
// Lista as contestações/feedbacks dos operadores (abertas primeiro) e permite responder
// aceitando ou mantendo a avaliação. Backend: core/views_memora_contestacao.py
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { STATUS_CONTESTACAO, TIPO_CONTESTACAO } from './ContestacaoAnalise';
import ReuniaoAnaliseModal from './ReuniaoAnaliseModal';

const API = process.env.REACT_APP_API_BASE;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } });
const fmtNota = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 1 }));

export function ItemContestacao({ item, onRespondido, onAbrirReuniao, destaque = false, mostrarReuniao = true }) {
  const [status, setStatus] = useState('');
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const respostaRef = useRef(null);
  const st = STATUS_CONTESTACAO[item.status] || STATUS_CONTESTACAO.aberta;
  const faltam = 5 - resposta.trim().length;
  const podeEnviar = !enviando && status && faltam <= 0;
  // o que falta para liberar o Responder (o botão desabilitado sozinho não explicava nada)
  const dica = !status
    ? 'Passo 1: escolha Aceitar ou Manter avaliação.'
    : faltam > 0
      ? `Passo 2: escreva a resposta para o operador${resposta.trim() ? ` (faltam ${faltam} caracteres)` : ''} e clique em Responder.`
      : 'Pronto: clique em Responder para enviar a decisão ao operador.';
  const escolher = (s) => {
    setStatus(s);
    setErro(null);
    setTimeout(() => respostaRef.current?.focus(), 0);
  };

  const responder = async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    setErro(null);
    try {
      const r = await axios.post(
        `${API}/api/reunioes/contestacao/${item.id}/responder/`,
        { status, resposta: resposta.trim() },
        authHeader(),
      );
      onRespondido(r.data?.item);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não foi possível responder. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="border rounded p-3" style={{ fontSize: '0.875rem', ...(destaque ? { borderColor: '#a30c8a', boxShadow: '0 0 0 2px rgba(163,12,138,0.18)' } : {}) }}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-1">
        <div>
          <span className="fw-bold" style={{ color: '#212529' }}>{item.operador || 'Operador'}</span>
          <span className="text-muted"> · {TIPO_CONTESTACAO[item.tipo] || item.tipo} · {item.created_at}</span>
        </div>
        <span className={`badge ${st.cls}`}>{st.label}</span>
      </div>
      <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
        {mostrarReuniao && (onAbrirReuniao ? (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
            style={{ fontSize: '0.8rem' }}
            onClick={() => onAbrirReuniao(item.codigo_reuniao)}
            title="Ver a reunião e a análise completa"
          >
            <i className="fas fa-video me-1"></i>
            {item.codigo_reuniao}
            {item.cliente_empresa && ` · ${item.cliente_empresa}`}
            {item.assunto && ` · ${item.assunto}`}
          </button>
        ) : (
          <>
            <i className="fas fa-video me-1"></i>
            {item.codigo_reuniao}
            {item.cliente_empresa && ` · ${item.cliente_empresa}`}
            {item.assunto && ` · ${item.assunto}`}
          </>
        ))}
        <span className={mostrarReuniao ? 'ms-2' : ''}>
          Nota da IA <strong>{fmtNota(item.nota_ia)}</strong>
          {item.nota_sugerida !== null && item.nota_sugerida !== undefined && (
            <> <i className="fas fa-arrow-right mx-1"></i>sugerida <strong>{fmtNota(item.nota_sugerida)}</strong></>
          )}
        </span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{item.motivo}</div>

      {item.status === 'aberta' ? (
        <div className="mt-3">
          <div className="d-flex flex-wrap gap-2 mb-2">
            <button
              type="button"
              className={`btn btn-sm ${status === 'aceita' ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => escolher('aceita')}
            >
              <i className="fas fa-check me-1"></i>Aceitar
            </button>
            <button
              type="button"
              className={`btn btn-sm ${status === 'mantida' ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => escolher('mantida')}
            >
              <i className="fas fa-balance-scale me-1"></i>Manter avaliação
            </button>
          </div>
          <textarea
            ref={respostaRef}
            className="form-control form-control-sm mb-2"
            rows={2}
            maxLength={2000}
            value={resposta}
            onChange={(e) => setResposta(e.target.value)}
            placeholder="Resposta para o operador (obrigatória)"
          />
          {erro && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: '0.8rem' }}>{erro}</div>}
          <div className="d-flex justify-content-between align-items-center gap-2">
            <span className={podeEnviar ? 'text-success' : 'text-muted'} style={{ fontSize: '0.78rem' }}>
              <i className={`fas ${podeEnviar ? 'fa-circle-check' : 'fa-circle-info'} me-1`}></i>{dica}
            </span>
            <button type="button" className="btn btn-sm btn-primary text-nowrap" onClick={responder} disabled={!podeEnviar}>
              {enviando
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Enviando...</>
                : <><i className="fas fa-reply me-1"></i>Responder</>}
            </button>
          </div>
        </div>
      ) : item.resposta && (
        <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', borderLeft: '3px solid #6c757d' }}>
          <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>
            <i className="fas fa-reply me-1"></i>Resposta{item.respondido_por ? ` de ${item.respondido_por}` : ''}{item.respondido_em ? ` · ${item.respondido_em}` : ''}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{item.resposta}</div>
        </div>
      )}
    </div>
  );
}

export default function ContestacoesGestor() {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarRespondidas, setMostrarRespondidas] = useState(false);
  const [codigoAberto, setCodigoAberto] = useState(null);
  const fecharReuniao = useCallback(() => setCodigoAberto(null), []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await axios.get(`${API}/api/reunioes/contestacoes/`, authHeader());
      setItens(r.data?.itens || []);
    } catch (e) {
      // 403 = não é adm: o card simplesmente não aparece
      if (e.response?.status !== 403) setErro('Não foi possível carregar as contestações.');
      setItens([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const onRespondido = (novo) => {
    if (!novo) { carregar(); return; }
    setItens((prev) => prev.map((x) => (x.id === novo.id ? novo : x)));
  };

  if (!carregando && !erro && itens.length === 0) return null;

  const abertas = itens.filter((x) => x.status === 'aberta');
  const respondidas = itens.filter((x) => x.status !== 'aberta');

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h6 className="fw-bold mb-0" style={{ color: '#212529' }}>
            <i className="fas fa-comment-dots me-2 text-muted"></i>Contestações das avaliações
            {abertas.length > 0 && <span className="badge bg-warning text-dark ms-2">{abertas.length} aberta{abertas.length > 1 ? 's' : ''}</span>}
          </h6>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={carregar} disabled={carregando}>
            <i className={`fas fa-sync-alt me-1 ${carregando ? 'fa-spin' : ''}`}></i>Atualizar
          </button>
        </div>

        {erro && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>{erro}</div>}
        {carregando && itens.length === 0 ? (
          <div className="text-muted" style={{ fontSize: '0.875rem' }}>
            <span className="spinner-border spinner-border-sm me-2"></span>Carregando...
          </div>
        ) : (
          <>
            {abertas.length === 0 ? (
              <div className="text-muted mb-2" style={{ fontSize: '0.875rem' }}>Nenhuma contestação aguardando resposta.</div>
            ) : (
              <div className="d-flex flex-column gap-2 mb-2">
                {abertas.map((it) => <ItemContestacao key={it.id} item={it} onRespondido={onRespondido} onAbrirReuniao={setCodigoAberto} />)}
              </div>
            )}
            {respondidas.length > 0 && (
              <>
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0 text-decoration-none"
                  onClick={() => setMostrarRespondidas((v) => !v)}
                >
                  <i className={`fas fa-chevron-${mostrarRespondidas ? 'up' : 'down'} me-1`}></i>
                  {mostrarRespondidas ? 'Ocultar' : 'Ver'} respondidas ({respondidas.length})
                </button>
                {mostrarRespondidas && (
                  <div className="d-flex flex-column gap-2 mt-2">
                    {respondidas.map((it) => <ItemContestacao key={it.id} item={it} onRespondido={onRespondido} onAbrirReuniao={setCodigoAberto} />)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {codigoAberto && <ReuniaoAnaliseModal codigo={codigoAberto} onClose={fecharReuniao} />}
    </div>
  );
}
